"use client"

import { RetrieveQuotePopup } from "@/shared/components/RetrieveQuotePopup/retrieve-quote-popup"
import { SkeletonPage } from "@dalmatech/ui"
import { useAnimalisContestPreview } from "@/shared/hooks/use-animalis-contest-preview"
import { formatQuoteResponse, useQuoteFetching } from "@/shared/hooks/use-quote-fetching"
import { ROUTES } from "@/shared/routing/routes"
import { getQuote } from "@/shared/http"
import type { QuoteReponse } from "@/shared/http/lead-api"
import { computeRedirectRoute } from "@/shared/utils/compute-redirect-route"
import { addQuoteIdToTrackJS } from "@/shared/utils/logs"
import {
  deleteQuoteIdCookie,
  getQuoteIdFromCookie,
  saveQuoteIdCookie,
} from "@/shared/utils/quote-cookie"
import { storeQuoteId } from "@/shared/utils/storage"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { type Route } from "next"
import { useRouter, useSearchParams } from "next/navigation"
import { usePostHog } from "posthog-js/react"
import { useEffect, useState, use } from "react"

type SearchParams = { [key: string]: string | string[] | undefined }
type Status = "checking" | "show-popup" | "default"

function MarketingDefaultRedirect({ searchParams }: { searchParams: SearchParams }) {
  useQuoteFetching(searchParams)
  return <SkeletonPage />
}

function MarketingRetrievePopup({
  petNames,
  cookieQuoteId,
  cookieQuote,
  onStartNew,
}: {
  petNames: string[]
  cookieQuoteId: string
  cookieQuote: QuoteReponse
  onStartNew: () => void
}) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [, send] = useActor(newSubscriptionFlowService)
  const posthog = usePostHog()

  const onRetrieve = () => {
    posthog.capture("retrieve_quote_decision", {
      has_retrieved: true,
      quote_id: cookieQuoteId,
    })
    const formattedQuote = formatQuoteResponse(cookieQuote, undefined, searchParams)
    storeQuoteId(cookieQuoteId)
    saveQuoteIdCookie(cookieQuoteId)
    addQuoteIdToTrackJS(cookieQuoteId)
    send({
      type: "UPDATE_QUOTE",
      quote: {
        ...formattedQuote,
        meta: {
          ...formattedQuote.meta,
          from_lp: true,
        },
      },
    })
    send({
      type: "SET_RC_CONTRACT_ID",
      rc_contract_id: cookieQuote.data[0]?.rc_contract_id,
    })

    const redirectRoute = computeRedirectRoute(cookieQuote)
    const petWithoutFormula = formattedQuote.pets?.find((p) => !p.pricing)
    const targetPet =
      redirectRoute === ROUTES.prepackages.path && petWithoutFormula
        ? petWithoutFormula
        : formattedQuote.pets?.[0]
    if (targetPet) {
      send({ type: "SET_CURRENT_PET_IDX", petId: targetPet.id, petIndex: targetPet.idx })
    }

    router.push(redirectRoute as Route, { scroll: true })
  }

  return (
    <>
      <SkeletonPage />
      <RetrieveQuotePopup
        open
        petNames={petNames}
        onRetrieve={onRetrieve}
        onStartNew={onStartNew}
      />
    </>
  )
}

export default function Page(props: { searchParams: Promise<SearchParams> }) {
  const searchParams = use(props.searchParams)
  const newSearchParams = useSearchParams()
  const posthog = usePostHog()
  // Capture the QA-only contest-banner preview flag here, while the marketing
  // query param is still in the URL (it is dropped by the redirect below). DLM-1775.
  useAnimalisContestPreview()
  const [status, setStatus] = useState<Status>("checking")
  const [popupData, setPopupData] = useState<{
    petNames: string[]
    cookieQuoteId: string
    cookieQuote: QuoteReponse
  } | null>(null)

  useEffect(() => {
    const urlQuoteId = newSearchParams.get("quote_id")

    if (urlQuoteId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- status state machine on mount (reads URL/cookie)
      setStatus("default")
      return
    }

    const cookieQuoteId = getQuoteIdFromCookie()
    if (!cookieQuoteId) {
      setStatus("default")
      return
    }

    void (async () => {
      try {
        const quote = await getQuote(cookieQuoteId)
        const names = quote.data.map((p) => p.pet_name).filter(Boolean)
        if (names.length > 0) {
          setPopupData({ petNames: names, cookieQuoteId, cookieQuote: quote })
          setStatus("show-popup")
          return
        }
        deleteQuoteIdCookie()
        setStatus("default")
      } catch {
        deleteQuoteIdCookie()
        setStatus("default")
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (status === "show-popup" && popupData) {
    const handleStartNew = () => {
      posthog.capture("retrieve_quote_decision", {
        has_retrieved: false,
        quote_id: popupData.cookieQuoteId,
      })
      deleteQuoteIdCookie()
      setPopupData(null)
      setStatus("default")
    }
    return <MarketingRetrievePopup {...popupData} onStartNew={handleStartNew} />
  }

  if (status === "default") {
    return <MarketingDefaultRedirect searchParams={searchParams} />
  }

  return <SkeletonPage />
}
